import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { AuthActions } from '../store/auth.actions';
import {
  AuthDestination,
  selectAuthDestination,
  selectReady,
} from '../store/auth.selectors';

function resolveAuthDestination(
  decide: (dest: AuthDestination, router: Router) => boolean | UrlTree,
): Observable<boolean | UrlTree> {
  const store = inject(Store);
  const router = inject(Router);
  store.dispatch(AuthActions.init());

  return store.select(selectReady).pipe(
    filter(Boolean),
    take(1),
    switchMap(() =>
      store.select(selectAuthDestination).pipe(
        filter((dest): dest is AuthDestination => dest != null),
        take(1),
        map((dest) => decide(dest, router)),
      ),
    ),
  );
}

/** Signed-in + onboarded routes (home). */
export const authGuard: CanActivateFn = () =>
  resolveAuthDestination((dest, router) =>
    dest === '/' ? true : router.createUrlTree([dest]),
  );

/** Login / register / forgot — only when logged out. */
export const guestGuard: CanActivateFn = () =>
  resolveAuthDestination((dest, router) =>
    dest === '/login' ? true : router.createUrlTree([dest]),
  );

/** Username onboarding. */
export const onboardingGuard: CanActivateFn = () =>
  resolveAuthDestination((dest, router) =>
    dest === '/onboarding' ? true : router.createUrlTree([dest]),
  );

/** Password recovery update screen. */
export const recoveryGuard: CanActivateFn = () =>
  resolveAuthDestination((dest, router) =>
    dest === '/update-password' ? true : router.createUrlTree([dest]),
  );
